import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useOfflineCache } from "@/hooks/useOfflineCache";
import { 
  WifiOff, 
  Wifi, 
  Database, 
  CloudOff, 
  Cloud, 
  RefreshCw,
  Check,
  AlertTriangle
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type SyncStatus = "synced" | "syncing" | "pending" | "error";

export function OfflineSyncIndicator() {
  const { isOnline, cachedReports } = useOfflineCache();
  const [syncStatus, setSyncStatus] = useState<SyncStatus>("synced");
  const [showDetails, setShowDetails] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  // Simulate sync status changes
  useEffect(() => {
    if (isOnline) {
      setSyncStatus("syncing");
      const timer = setTimeout(() => {
        setSyncStatus("synced");
        setLastSyncTime(new Date());
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      setSyncStatus("pending");
    }
  }, [isOnline]);

  const getSyncIcon = () => {
    switch (syncStatus) {
      case "synced":
        return <Check className="h-4 w-4 text-success" />;
      case "syncing":
        return <RefreshCw className="h-4 w-4 animate-spin text-primary" />;
      case "pending":
        return <CloudOff className="h-4 w-4 text-amber-500" />;
      case "error":
        return <AlertTriangle className="h-4 w-4 text-destructive" />;
    }
  };

  const getSyncMessage = () => {
    switch (syncStatus) {
      case "synced":
        return "Dados sincronizados";
      case "syncing":
        return "Sincronizando...";
      case "pending":
        return "Sincronização pendente";
      case "error":
        return "Erro de sincronização";
    }
  };

  // Offline banner
  if (!isOnline) {
    return (
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        className="fixed top-0 left-0 right-0 z-50"
      >
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 px-4">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <WifiOff className="h-5 w-5" />
              </motion.div>
              <div>
                <p className="font-medium">Você está offline</p>
                <p className="text-sm text-white/80">
                  Seus dados serão sincronizados quando a conexão voltar
                </p>
              </div>
            </div>
            
            {cachedReports.length > 0 && (
              <Badge 
                variant="secondary" 
                className="bg-white/20 text-white border-none gap-1.5"
              >
                <Database className="h-3 w-3" />
                {cachedReports.length} relatório{cachedReports.length > 1 ? "s" : ""} disponíve{cachedReports.length > 1 ? "is" : "l"}
              </Badge>
            )}
          </div>
        </div>
        
        {/* Progress indicator */}
        <div className="h-1 bg-white/20">
          <motion.div
            className="h-full bg-white/50"
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 3, repeat: Infinity }}
          />
        </div>
      </motion.div>
    );
  }

  // Online sync status indicator (compact)
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="fixed bottom-20 right-4 z-40"
      >
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowDetails(!showDetails)}
          className={cn(
            "gap-2 rounded-full shadow-lg bg-card",
            syncStatus === "synced" && "border-success/30",
            syncStatus === "syncing" && "border-primary/30",
            syncStatus === "pending" && "border-amber-500/30",
            syncStatus === "error" && "border-destructive/30"
          )}
        >
          {isOnline ? (
            <Wifi className="h-4 w-4 text-success" />
          ) : (
            <WifiOff className="h-4 w-4 text-amber-500" />
          )}
          {getSyncIcon()}
          <span className="text-xs">{getSyncMessage()}</span>
        </Button>

        {/* Details popup */}
        <AnimatePresence>
          {showDetails && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="absolute bottom-full right-0 mb-2 w-64 p-4 bg-card border border-border rounded-xl shadow-xl"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Status da conexão</span>
                  <Badge variant={isOnline ? "default" : "secondary"}>
                    {isOnline ? "Online" : "Offline"}
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Dados em cache</span>
                  <span>{cachedReports.length} relatórios</span>
                </div>
                
                {lastSyncTime && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Última sincronização</span>
                    <span>{lastSyncTime.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                )}
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full gap-2"
                  onClick={() => {
                    setSyncStatus("syncing");
                    setTimeout(() => {
                      setSyncStatus("synced");
                      setLastSyncTime(new Date());
                    }, 1500);
                  }}
                >
                  <RefreshCw className="h-4 w-4" />
                  Forçar sincronização
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}
